import { Flex, Select } from 'antd';
import styles from '@/components/custom-filters/custom-filters.module.scss';
import { DashboardFilters } from '@/services/dashboard-service/dashboard.service.types.ts';
import { CallLogFilters } from '@/services/call-log-service/call-log-service.types';
import { useMemo } from 'react';
import { debounce } from '@/utils/debounce/debounce';
import { SEARCH_DELAY } from '../conversations-header/conversations-header.constants';
import clsx from 'clsx';

interface CustomFiltersProps {
  onChangeFilters: (filters: DashboardFilters | CallLogFilters) => void;
  filters: DashboardFilters | CallLogFilters;
  companiesList: string[];
  defaultCompanyValue?: string;
  onSearchQueryChange: (query: string) => void;
  searchResults?: string[];
  isSearchLoading?: boolean;
  searchError?: Error | null;
  customClassName?: string;
  disabled?: boolean;
}

export function CustomFilters({
  onChangeFilters,
  filters,
  companiesList,
  defaultCompanyValue,
  onSearchQueryChange,
  searchResults,
  isSearchLoading,
  searchError,
  customClassName,
  disabled,
}: CustomFiltersProps) {
  const debouncedSearchChange = useMemo(
    () => debounce(onSearchQueryChange, SEARCH_DELAY),
    [onSearchQueryChange]
  );

  const handleSearch = (value: string) => {
    debouncedSearchChange(value);
  };

  const handleCompanyChange = (value: string | null) => {
    onChangeFilters({
      ...filters,
      company_name: value ?? defaultCompanyValue,
    });
    onSearchQueryChange('');
  };

  const allCompanies = searchResults && searchResults.length > 0 ? searchResults : companiesList;

  const uniqueCompanies = [...new Set(allCompanies)];

  const mappedCompaniesList = [
    ...uniqueCompanies.map((company) => ({
      value: company,
      label: company,
    })),
  ];

  return (
    <Flex>
      <Select
        className={clsx(styles.select, customClassName)}
        classNames={{
          popup: {
            root: styles.dropdown,
          },
        }}
        allowClear={filters.company_name == defaultCompanyValue ? false : true}
        options={mappedCompaniesList}
        value={filters.company_name}
        defaultValue={defaultCompanyValue}
        showSearch
        onChange={handleCompanyChange}
        onSearch={handleSearch}
        placeholder="Company"
        disabled={disabled}
        loading={isSearchLoading}
        notFoundContent={
          isSearchLoading
            ? 'Searching...'
            : searchError
              ? 'Search not available - showing all companies'
              : 'No companies found'
        }
      />
    </Flex>
  );
}
